/**
 * `/settings/support` — landing page for the eleven Support CRM config screens.
 *
 * Cards are filtered by grant: these settings are deliberately assignable
 * separately from ticket work, so a team lead can edit tickets without being
 * handed the routing engine. Showing them a card that bounces to /forbidden
 * would misrepresent what they have.
 */
import { Link } from 'react-router-dom';
import {
  Bot,
  ChevronRight,
  Gauge,
  Layers,
  Mail,
  Megaphone,
  MessageCircle,
  MessageSquareText,
  Share2,
  ShieldAlert,
  Slash,
  Zap,
} from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { useAccess } from '@/features/AccessProvider';

const SECTIONS = [
  {
    to: '/settings/support/routing',
    slug: 'crm.settings.routing',
    title: 'Routing Matrix',
    description:
      'Rules + escalation tiers controlling who is auto-assigned to each ticket and how SLA clocks behave.',
    icon: ShieldAlert,
  },
  {
    to: '/settings/support/sla',
    slug: 'crm.settings.sla',
    title: 'SLA & Breach Policy',
    description:
      'Operational stage limits, breach recording grace, and ticket priority bands — the three thresholds that control when a breach becomes a ticket.',
    icon: Gauge,
  },
  {
    to: '/settings/support/reason-codes',
    slug: 'crm.settings.reason_codes',
    title: 'Reason codes',
    description:
      'Manage CRM reason taxonomy (SLA, refunds, cancellations, etc.). Active codes appear in ticket forms; reserved SLA breach tags stay protected.',
    icon: Layers,
  },
  {
    to: '/settings/support/cancellations',
    slug: 'crm.settings.cancellations',
    title: 'Cancellation Sources',
    description:
      'Toggle which order-cancellation paths auto-open a CRM ticket and which priority they default to.',
    icon: Slash,
  },
  {
    to: '/settings/support/email',
    slug: 'crm.settings.email',
    title: 'Email (SMTP + IMAP)',
    description: 'Gmail SMTP for outbound plus IMAP inbound so customer emails become conversations.',
    icon: Mail,
  },
  {
    to: '/settings/support/whatsapp',
    slug: 'crm.settings.whatsapp',
    title: 'WhatsApp (staff alerts)',
    description:
      'Staff ticket assignment/escalation alerts. Customer WhatsApp lives under Meta Channels.',
    icon: MessageCircle,
  },
  {
    to: '/settings/support/meta',
    slug: 'crm.settings.meta',
    title: 'Meta Channels',
    description:
      'Customer WhatsApp number, Instagram, and Facebook Messenger — inbound webhooks + replies.',
    icon: Share2,
  },
  {
    to: '/settings/support/ai',
    slug: 'crm.settings.ai',
    title: 'AI Agent',
    description:
      'Hybrid analyze-and-act policy: auto-reply, draft for approval, or escalate to a CRM ticket.',
    icon: Bot,
  },
  {
    to: '/settings/support/canned-replies',
    slug: 'crm.settings.canned_replies',
    title: 'Canned Replies',
    description:
      'Saved snippets agents insert with / in the inbox composer — per-channel or global, with {customer_name} personalisation.',
    icon: Zap,
  },
  {
    to: '/settings/support/engagement',
    slug: 'crm.settings.engagement',
    title: 'Engagement & Operations',
    description:
      'WhatsApp re-engagement follow-ups for abandoned enquiries, media retention limits, and inbox notification defaults.',
    icon: Megaphone,
  },
  {
    to: '/settings/support/templates',
    slug: 'crm.settings.templates',
    title: 'Notification Templates',
    description:
      'Edit subject + Markdown body for each event-kind. Variables come from a backend whitelist.',
    icon: MessageSquareText,
  },
];

export function CrmSettingsIndexPage() {
  const { can, isSuperuser } = useAccess();
  const sections = SECTIONS.filter((s) => isSuperuser || can(s.slug));

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Configure how support routes, escalates and notifies. Changes apply immediately to live customer
        conversations.
      </p>

      {sections.length === 0 ? (
        <EmptyState
          title="No support settings assigned"
          description="Ask a superuser to grant the Support CRM configuration resources you need."
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {sections.map((s) => {
            const Icon = s.icon;
            return (
              <Link key={s.to} to={s.to} className="group focus-ring rounded-lg">
                <Card className="h-full transition-shadow hover:shadow-kpi">
                  <CardBody className="flex items-start justify-between gap-3 p-4">
                    <div className="flex items-start gap-3">
                      <span className="rounded-md border border-border bg-surface p-2 text-primary">
                        <Icon className="h-4 w-4" aria-hidden />
                      </span>
                      <div>
                        <h3 className="text-sm font-semibold group-hover:text-primary">{s.title}</h3>
                        <p className="mt-1 text-xs text-muted-foreground">{s.description}</p>
                      </div>
                    </div>
                    <ChevronRight
                      className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
                      aria-hidden
                    />
                  </CardBody>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
